/**
 * What counts as an arrival. A run occupies one row for its whole life — QUEUED, RUNNING and
 * SUCCEEDED share an id — so an arrival is a status this tab hasn't seen, not an unseen id.
 */
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_STATUS,
  type Notification,
  type NotificationStatus,
} from 'types/modules/notification-center/notification'

import { NOTIFICATION_POLLING } from './notificationPolling'

/** A run past these is finished; anything else can still change on its own. */
const TERMINAL_STATUSES: readonly NotificationStatus[] = Object.freeze([
  NOTIFICATION_STATUS.SUCCEEDED,
  NOTIFICATION_STATUS.FAILED,
  NOTIFICATION_STATUS.PARTIAL,
])

export const isTerminalStatus = (status: NotificationStatus): boolean => TERMINAL_STATUSES.includes(status)

/** Row id → the status this tab last saw on it. */
export type SeenStatuses = ReadonlyMap<string, NotificationStatus>

/**
 * A job is only worth a toast once it settles — its queued and running states are the inbox's
 * business. A customer event has no lifecycle, so the first sighting *is* the event.
 */
export const shouldToast = (notification: Notification, seen: SeenStatuses): boolean => {
  const lastSeenStatus = seen.get(notification.id)
  if (lastSeenStatus === notification.status) return false
  if (notification.category === NOTIFICATION_CATEGORY.JOB) return isTerminalStatus(notification.status)
  return lastSeenStatus === undefined
}

/**
 * Notifications this tab's user deliberately made unread. Marking unread bumps `lastActivityAt` and puts
 * the row back in the unread list, which is indistinguishable from an arrival — but the user already read
 * it and chose to keep it, so announcing it again would be the app arguing with them. Tab-scoped on
 * purpose: it is a record of an intent expressed here, not a preference to persist.
 */
const locallyUnread = new Set<string>()

export const markLocallyUnread = (ids: readonly string[]): void => ids.forEach(id => locallyUnread.add(id))

/** Separate from `shouldToast`: a settled run refreshes what it changed even when it stays silent. */
export const arrivalsIn = (notifications: readonly Notification[], seen: SeenStatuses): Notification[] =>
  notifications.filter(notification => !locallyUnread.has(notification.id) && seen.get(notification.id) !== notification.status)

/**
 * What earns the faster poll. Read off the list rather than the request that started it, so a run
 * from another tab — or from the async path with no UI at all — is watched the same.
 */
export const hasRunInFlight = (
  notifications: readonly Notification[],
  now: number,
  windowMs: number = NOTIFICATION_POLLING.inFlightWindowMs
): boolean =>
  notifications.some(
    notification =>
      notification.category === NOTIFICATION_CATEGORY.JOB &&
      !isTerminalStatus(notification.status) &&
      now - Date.parse(notification.lastActivityAt) < windowMs
  )
